import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeader from './Navigation/PageHeader';
import StaffDetailContent from './StaffDetailContent';
import * as staffService from '../services/staffService';
import * as caseService from '../services/caseService';
import * as exportService from '../services/exportService';
import { ExportModal } from './Export';

const { FiArrowLeft, FiUsers, FiDownload } = FiIcons;

/**
 * Staff Details Page Component
 * Displays full details of a staff member with tabs for overview, cases, timeline, and performance
 */
const StaffDetails = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    loadStaffData();
  }, [staffId]);

  const loadStaffData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load staff data and all cases
      const [staffData, casesData] = await Promise.all([
        staffService.getStaffById(staffId),
        caseService.getAllCases()
      ]);

      if (!staffData) {
        setError('Staff member not found');
        return;
      }

      setStaff(staffData);
      // Filter cases for this staff member
      setCases(casesData.filter(c => c.staff_id === staffId));
    } catch (err) {
      console.error('Error loading staff data:', err);
      setError('Failed to load staff data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/staff');
  };

  const handleExport = async (exportType, options) => {
    try {
      setExportLoading(true);
      await exportService.exportDocument('staff', staffId, exportType, options);
    } catch (err) {
      console.error('Error exporting staff timeline:', err);
      throw err;
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Staff Details"
          subtitle="Loading staff information..."
          icon={FiUsers}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !staff) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Staff Details"
          subtitle="Error loading staff information"
          icon={FiUsers}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-red-600 mb-4">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {error || 'Staff member not found'}
            </h3>
            <p className="text-gray-600 mb-6">
              The staff member you're looking for doesn't exist or you don't have permission to view it.
            </p>
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Back to Staff List
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={staff.full_name}
        subtitle={`${staff.position} • ${staff.department}`}
        icon={FiUsers}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button and Export */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            <span>Back to Staff List</span>
          </button>
          <button
            onClick={() => setShowExportModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <SafeIcon icon={FiDownload} />
            <span>Export</span>
          </button>
        </div>

        {/* Main Content Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6"
        >
          <StaffDetailContent staff={staff} cases={cases} />
        </motion.div>
      </div>

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        entityType="staff"
        entityName={`${staff.employee_id} - ${staff.full_name}`}
        isLoading={exportLoading}
      />
    </div>
  );
};

export default StaffDetails;
